import { AlertCircle, Search, Users } from "lucide-react";
import { useMemo, useState } from "react";

import { CardGrid } from "components/CardGrid";
import { EmptyState, LoadingCards } from "components/states";
import { ToggleChip } from "components/ToggleChip";
import { Button } from "components/ui/button";

import { AttendeeCard } from "./AttendeeCard";
import { AttendeeFilters, CHIP_OFF, CHIP_ON } from "./AttendeeFilters";
import { AttendeeHeader } from "./AttendeeHeader";
import {
  activeFilterCount,
  distinctValues,
  filterDirectory,
  NO_FILTERS,
  summariseSignals,
  withoutMissingSignals,
  type DirectoryFilters,
  type DirectoryViewer,
} from "./directory-filters";
import { EventPersonSheet } from "./EventPersonSheet";
import { SignalDirectory } from "./SignalDirectory";
import { useDirectory, useTracks } from "./use-event-content";
import { useSignals, useSignalsByAttendee } from "./use-signals";

type View = "people" | "signals";

export function AttendeesSection({
  eventId,
  ownAttendeeId,
}: {
  eventId: string;
  /** The viewer's roster row, for the "same as me" filters. */
  ownAttendeeId?: string;
}) {
  const directory = useDirectory(eventId);
  const signalsState = useSignals(eventId);
  const tracksState = useTracks(eventId);
  const signalsByAttendee = useSignalsByAttendee(eventId);
  const [chosen, setFilters] = useState<DirectoryFilters>(NO_FILTERS);
  const [panelOpen, setPanelOpen] = useState(false);
  const [view, setView] = useState<View>("people");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const attendees = useMemo(
    () => (directory.status === "success" ? directory.data : []),
    [directory],
  );
  const signals = useMemo(
    () => (signalsState.status === "success" ? signalsState.data : []),
    [signalsState],
  );
  // What the person chose, minus a signal an organiser has since deleted.
  const filters = useMemo(
    () => withoutMissingSignals(chosen, signals),
    [chosen, signals],
  );
  const tracks = tracksState.status === "success" ? tracksState.data : [];

  const viewer = useMemo<DirectoryViewer>(() => {
    const own = ownAttendeeId
      ? attendees.find((attendee) => attendee.id === ownAttendeeId)
      : undefined;
    return {
      attendeeId: ownAttendeeId,
      signalIds: ownAttendeeId
        ? (signalsByAttendee.get(ownAttendeeId) ?? []).map((pick) => pick.id)
        : [],
      trackId: own?.trackId,
    };
  }, [attendees, ownAttendeeId, signalsByAttendee]);

  const visible = useMemo(
    () => filterDirectory(attendees, filters, signalsByAttendee, viewer),
    [attendees, filters, signalsByAttendee, viewer],
  );
  const companies = useMemo(
    () => distinctValues(attendees, "company"),
    [attendees],
  );
  const summaries = useMemo(
    () => summariseSignals(signals, attendees, signalsByAttendee),
    [signals, attendees, signalsByAttendee],
  );
  // The signals a card shows instead of its badge while a signal filter is
  // on, so a card says why it matched.
  const highlighted = useMemo(
    () =>
      new Set([
        ...filters.signalIds,
        ...(filters.sameSignalsAsMe ? viewer.signalIds : []),
      ]),
    [filters.signalIds, filters.sameSignalsAsMe, viewer.signalIds],
  );
  const trackById = useMemo(
    () => new Map(tracks.map((track, index) => [track.id, { track, index }])),
    [tracks],
  );
  // A track that no longer exists cannot be matched on, so the chip hides.
  const viewerTrackExists =
    viewer.trackId !== undefined &&
    tracks.some((track) => track.id === viewer.trackId);

  // The tab's head stays up through every state, so it does not jump when
  // the directory arrives.
  if (directory.status === "loading") {
    return (
      <div className="space-y-5">
        <AttendeeHeader />
        <LoadingCards cards={4} mode="grid" />
      </div>
    );
  }
  if (directory.status === "error") {
    return (
      <div className="space-y-5">
        <AttendeeHeader />
        <EmptyState icon={AlertCircle} title="Could not load attendees">
          {directory.error}
        </EmptyState>
      </div>
    );
  }
  if (attendees.length === 0) {
    return (
      <div className="space-y-5">
        <AttendeeHeader count={0} />
        <EmptyState icon={Users} title="No attendees yet">
          Everybody on the attendee list appears here, from the moment an
          organiser adds them.
        </EmptyState>
      </div>
    );
  }

  const filtered =
    filters.query.trim() !== "" || activeFilterCount(filters) > 0;

  return (
    <div className="space-y-5" data-testid="attendees-content">
      <AttendeeHeader count={attendees.length} />

      {signals.length > 0 && (
        <div
          className="flex gap-2"
          role="group"
          aria-label="Browse attendees by"
        >
          <ToggleChip
            selected={view === "people"}
            onClick={() => setView("people")}
            className={`min-h-11 rounded-full px-4 text-[0.9375rem] ${view === "people" ? CHIP_ON : CHIP_OFF}`}
            data-testid="attendee-view-people"
          >
            People
          </ToggleChip>
          <ToggleChip
            selected={view === "signals"}
            onClick={() => setView("signals")}
            className={`min-h-11 rounded-full px-4 text-[0.9375rem] ${view === "signals" ? CHIP_ON : CHIP_OFF}`}
            data-testid="attendee-view-signals"
          >
            Signals
          </ToggleChip>
        </div>
      )}

      {view === "signals" && signals.length > 0 ? (
        <SignalDirectory
          summaries={summaries}
          mySignalIds={new Set(viewer.signalIds)}
          onSelect={(signalId) => {
            setFilters({ ...NO_FILTERS, signalIds: [signalId] });
            setPanelOpen(true);
            setView("people");
          }}
        />
      ) : (
        <>
          <AttendeeFilters
            filters={filters}
            onChange={setFilters}
            panelOpen={panelOpen}
            onPanelOpenChange={setPanelOpen}
            signals={signals}
            tracks={tracks}
            companies={companies}
            canMatchMySignals={viewer.signalIds.length > 0}
            canMatchMyTrack={viewerTrackExists}
          />

          {/* Always mounted, so a screen reader announces the new count. */}
          <p
            className="text-sm text-muted-foreground empty:hidden"
            role="status"
            data-testid="attendee-results-count"
          >
            {filtered ? `${visible.length} of ${attendees.length} shown` : ""}
          </p>

          {visible.length === 0 ? (
            <EmptyState
              icon={Search}
              title="No attendees match"
              action={
                activeFilterCount(filters) > 0 ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11"
                    data-testid="attendee-empty-clear"
                    onClick={() =>
                      setFilters({ ...NO_FILTERS, query: filters.query })
                    }
                  >
                    Clear filters
                  </Button>
                ) : undefined
              }
            >
              Try a different search or clear the filters.
            </EmptyState>
          ) : (
            // One column on a phone, two from `md`: a card is wider than it
            // is tall, so three columns never fit.
            <CardGrid
              className="gap-2.5 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2"
              testId="attendee-list"
            >
              {visible.map((attendee) => (
                <AttendeeCard
                  key={attendee.id}
                  attendee={attendee}
                  track={trackById.get(attendee.trackId ?? "")?.track}
                  trackIndex={trackById.get(attendee.trackId ?? "")?.index}
                  picks={signalsByAttendee.get(attendee.id) ?? []}
                  highlighted={highlighted}
                  onSelect={() => setSelectedId(attendee.id)}
                />
              ))}
            </CardGrid>
          )}
        </>
      )}

      <EventPersonSheet
        eventId={eventId}
        person={selectedId ? { attendeeId: selectedId } : null}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}
